import {
  createColumnHelper,
  createSortedRowModel,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
  useTable,
  type ColumnDef,
  type RowData,
} from '@tanstack/react-table'
import type { KeyboardEvent, ReactNode } from 'react'
import { IconoVolver } from './iconos'
import { iconoDeColumna } from './iconosColumna'

/**
 * Tabla de datos generica sobre TanStack Table. La ordenacion se resuelve en
 * cliente y ordena la página que recibe (15 filas, `componentes/paginacion.ts`):
 * quien la usa le pasa ya la página, de la API o troceada en el navegador. Sin
 * scroll virtualizado: 15 nodos no justifican la complejidad.
 */
const caracteristicas = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
})

export type CaracteristicasTabla = typeof caracteristicas

/**
 * `any` en el tercer parametro a proposito: cada columna tiene su propio tipo de
 * valor y `ColumnDef` es invariante en el, asi que fijarlo en `unknown` haria
 * que un array mixto de columnas dejara de encajar.
 */
// oxlint-disable-next-line no-explicit-any
export type ColumnaTabla<T extends RowData> = ColumnDef<CaracteristicasTabla, T, any>

/**
 * Constructor de columnas ya atado a las caracteristicas de esta tabla, para
 * que quien la use no tenga que repetir el parametro de tipo.
 */
export function ayudanteColumnas<T extends RowData>() {
  return createColumnHelper<CaracteristicasTabla, T>()
}

interface Props<T extends RowData> {
  datos: T[]
  columnas: Array<ColumnaTabla<T>>
  onSeleccionar?: (elemento: T) => void
  seleccionadoId?: string | null
  estadoVacio: ReactNode
  /** Por defecto se usa la propiedad `id`, que es lo que devuelve la API. */
  obtenerId?: (elemento: T) => string
  etiqueta?: string
}

function idPorDefecto(elemento: unknown, indice: number): string {
  const id = (elemento as { id?: unknown }).id
  return id === undefined || id === null ? String(indice) : String(id)
}

// Trazabis DS: celda 11 x 14, texto principal y cifras tabulares.
const CLASE_CELDA = 'px-3.5 py-2.5 text-[13px] tabular-nums text-tinta'

/** Icono de la cabecera (lila, 13 px), si su texto esta en la tabla canonica. */
function IconoCabecera({ cabecera }: { cabecera: unknown }) {
  const Icono = iconoDeColumna(cabecera)
  return Icono ? <Icono aria-hidden="true" className="size-[13px] shrink-0 text-[var(--tz-ic-accent)]" /> : null
}

export function TablaDatos<T extends RowData>({
  datos,
  columnas,
  onSeleccionar,
  seleccionadoId,
  estadoVacio,
  obtenerId,
  etiqueta = 'Tabla de datos',
}: Props<T>) {
  const tabla = useTable({
    features: caracteristicas,
    columns: columnas,
    data: datos,
    getRowId: (fila, indice) => (obtenerId ? obtenerId(fila) : idPorDefecto(fila, indice)),
  })

  // Sin datos no se pinta ni la cabecera: unas columnas sueltas sobre el vacio
  // parecen un fallo de carga en vez de un resultado legitimo.
  if (datos.length === 0) return <>{estadoVacio}</>

  const seleccionar = (elemento: T) => {
    if (onSeleccionar) onSeleccionar(elemento)
  }

  const alPulsarTecla = (evento: KeyboardEvent<HTMLTableRowElement>, elemento: T) => {
    if (evento.key !== 'Enter' && evento.key !== ' ') return
    // El espacio haria scroll de la pagina y Enter podria enviar un formulario.
    evento.preventDefault()
    seleccionar(elemento)
  }

  return (
    // role="grid": las filas son seleccionables y alcanzables con el teclado,
    // que es justo lo que `grid` promete y `table` no.
    <div className="overflow-x-auto rounded-xl border border-borde bg-superficie">
      <table role="grid" aria-label={etiqueta} className="min-w-full border-collapse">
        <thead className="bg-[var(--tz-th-bg)]">
          {tabla.getHeaderGroups().map((grupo) => (
            <tr key={grupo.id}>
              {grupo.headers.map((cabecera) => {
                const ordenable = cabecera.column.getCanSort()
                const sentido = cabecera.column.getIsSorted()
                return (
                  <th
                    key={cabecera.id}
                    scope="col"
                    aria-sort={
                      sentido === 'asc'
                        ? 'ascending'
                        : sentido === 'desc'
                          ? 'descending'
                          : undefined
                    }
                    className="border-b border-borde px-3.5 py-2.5 text-left text-[11.5px] font-semibold uppercase tracking-[0.04em] whitespace-nowrap text-[var(--tz-th-text)]"
                  >
                    {cabecera.isPlaceholder ? null : ordenable ? (
                      <button
                        type="button"
                        onClick={cabecera.column.getToggleSortingHandler()}
                        className="flex items-center gap-1.5 rounded-sm uppercase transition-colors hover:text-tinta"
                      >
                        <IconoCabecera cabecera={cabecera.column.columnDef.header} />
                        <tabla.FlexRender header={cabecera} />
                        {/* Chevron del modulo de iconos girado, en vez de una
                            flecha de texto: sin emojis ni caracteres sueltos. */}
                        {sentido ? (
                          <IconoVolver
                            aria-hidden="true"
                            className={`size-3 text-tinta-2 ${
                              sentido === 'asc' ? 'rotate-90' : '-rotate-90'
                            }`}
                          />
                        ) : null}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        <IconoCabecera cabecera={cabecera.column.columnDef.header} />
                        <tabla.FlexRender header={cabecera} />
                      </span>
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {tabla.getRowModel().rows.map((fila) => {
            const seleccionada = seleccionadoId != null && fila.id === seleccionadoId
            return (
              <tr
                key={fila.id}
                aria-selected={seleccionada}
                tabIndex={0}
                onClick={() => seleccionar(fila.original)}
                onKeyDown={(evento) => alPulsarTecla(evento, fila.original)}
                className={[
                  'cursor-pointer border-b border-borde-suave outline-none transition-colors last:border-b-0',
                  'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-foco',
                  seleccionada ? 'bg-acento-suave' : 'hover:bg-[var(--tz-hover)]',
                ].join(' ')}
              >
                {fila.getAllCells().map((celda) => (
                  <td key={celda.id} className={CLASE_CELDA}>
                    <tabla.FlexRender cell={celda} />
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
